import { Component, EventEmitter, Input, Output, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, debounceTime } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { AppListFilterDialogComponent } from './list-filter-dialog.component';
import { FilterField } from '@ukam/core/filters/models/filter-field';
import { FilterFieldItem } from '@ukam/core/filters/models/filter-field-item';
import { NgScrollbarModule } from 'ngx-scrollbar';

@Component({
  selector: 'app-list-filter',
  templateUrl: './list-filter.component.html',
  imports: [
    FormsModule,
    MaterialModule,
    TranslocoModule,
    NgScrollbarModule,
    CdkDragHandle],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppListFilterComponent), multi: true },
  ],
})
export class AppListFilterComponent implements ControlValueAccessor {

  @Input() allowRemoveItem = true;

  @Output() removed = new EventEmitter<void>();

  private dialog = inject(MatDialog);

  field: FilterField | null = null;
  items: FilterFieldItem[] = [];

  disabled = signal(false);

  private onChange: (value: FilterField | null) => void = () => { };
  private onTouched: () => void = () => { };

  private changeSubject = new Subject<FilterField>();
  private changeSubscription = this.changeSubject.pipe(debounceTime(100)).subscribe(field => {
    this.onChange(field);
    this.onTouched();
  });

  get hasItems(): boolean {
    return this.items.length > 0;
  }

  openDialog(): void {
    if (this.disabled()) return;

    const ref = this.dialog.open<AppListFilterDialogComponent, FilterField>(
      AppListFilterDialogComponent,
      {
        width: '480px',
        maxWidth: '90vw',
        autoFocus: false,
        data: this.field
      },
    );

    ref.afterClosed().subscribe(result => {
      if (!result) return;
      this.setItems(result);
    });
  }

  toggleItem(index: number): void {
    const items = [...this.items];
    items[index] = { ...items[index], selected: !items[index].selected };
    this.setItems(items);
  }

  removeItem(index: number): void {
    this.setItems(this.items.filter((_, i) => i !== index));
  }

  selectAll(): void {
    this.setItems(this.items.map(i => ({ ...i, selected: true })));
  }

  deselectAll(): void {
    this.setItems(this.items.map(i => ({ ...i, selected: false })));
  }

  clearItems(): void {
    this.setItems([]);
  }

  private setItems(items: FilterFieldItem[]): void {
    this.items = items;
    this.emit({ items, operator: items.length > 0 ? 'in' : undefined });
  }

  private emit(patch: Partial<FilterField>): void {
    if (!this.field) return;
    this.field = { ...this.field, ...patch };
    this.changeSubject.next(this.field);
  }

  writeValue(value: FilterField | null): void {
    this.field = value ?? null;
    this.items = this.sortBySelected(this.field?.items ?? []);
  }

  private sortBySelected(items: FilterFieldItem[]): FilterFieldItem[] {
    return [...items].sort((a, b) => Number(!!b.selected) - Number(!!a.selected));
  }

  registerOnChange(fn: (value: FilterField | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
